import config from '@/config'
import bgmConfig from '@/config/bgm'

// BGM管理器
class BGMManager {
  constructor() {
    this.audio = null
    this.currentBGM = null
    this.isPlaying = false
    this.volume = 0.5 // 默认音量50%
    this.bgmList = []
    this.currentIndex = 0
    this.shouldAutoResume = false // 是否应该自动恢复播放（用于前后台切换）
  }

  // 初始化BGM列表（从配置文件加载）
  initBGMList() {
    // 从配置文件加载BGM列表
    this.bgmList = [...bgmConfig]
    
    // 尝试从localStorage加载用户保存的BGM列表（用于动态添加的BGM）
    const savedList = localStorage.getItem('bgm_list')
    if (savedList) {
      try {
        const customList = JSON.parse(savedList)
        // 合并配置文件和用户自定义列表（去重）
        const mergedList = [...this.bgmList]
        customList.forEach(bgm => {
          if (!mergedList.find(b => b.path === bgm.path)) {
            mergedList.push(bgm)
          }
        })
        this.bgmList = mergedList
      } catch (e) {
        console.warn('加载BGM列表失败，使用默认列表')
      }
    }
    
    return this.bgmList
  }

  // 更新BGM列表（可以通过API或手动添加）
  updateBGMList(list) {
    this.bgmList = list
    localStorage.setItem('bgm_list', JSON.stringify(list))
  }

  // 加载BGM
  loadBGM(bgmItem) {
    return new Promise((resolve, reject) => {
      if (!bgmItem || !bgmItem.path) {
        reject(new Error('BGM信息不完整'))
        return
      }

      // 如果正在播放相同的BGM，直接返回
      if (this.audio && this.currentBGM?.path === bgmItem.path && this.isPlaying) {
        resolve(this.audio)
        return
      }

      // 停止当前BGM
      this.stop()

      // 创建新的Audio对象
      const audio = new Audio(bgmItem.path)
      audio.preload = 'auto'
      audio.loop = true // 循环播放
      audio.volume = this.volume

      audio.addEventListener('canplaythrough', () => {
        this.audio = audio
        this.currentBGM = bgmItem
        this.currentIndex = this.bgmList.findIndex(bgm => bgm.path === bgmItem.path)
        resolve(audio)
      })

      audio.addEventListener('error', (error) => {
        reject(error)
      })

      // 加载音频
      audio.load()
    })
  }

  // 播放BGM
  async play(bgmItem = null) {
    try {
      let targetBGM = bgmItem

      // 如果没有指定BGM，使用当前BGM或列表中的第一个
      if (!targetBGM) {
        if (this.currentBGM) {
          targetBGM = this.currentBGM
        } else if (this.bgmList.length > 0) {
          targetBGM = this.bgmList[0]
        } else {
          throw new Error('没有可用的BGM')
        }
      }

      // 如果正在播放相同的BGM，直接返回
      if (this.audio && this.currentBGM?.path === targetBGM.path && this.isPlaying) {
        return
      }

      // 加载BGM
      await this.loadBGM(targetBGM)

      // 播放
      if (this.audio) {
        await this.audio.play()
        this.isPlaying = true
      }
    } catch (error) {
      console.error('播放BGM失败:', error)
      throw error
    }
  }

  // 停止BGM
  stop() {
    if (this.audio) {
      this.audio.pause()
      this.audio.currentTime = 0
      this.isPlaying = false
    }
  }

  // 暂停BGM
  pause(autoResume = false) {
    if (this.audio) {
      // 如果是因为前后台切换而暂停，且当前正在播放，标记为应该自动恢复
      if (autoResume && this.isPlaying) {
        this.shouldAutoResume = true
      } else {
        this.shouldAutoResume = false
      }
      this.audio.pause()
      this.isPlaying = false
    }
  }

  // 继续播放BGM
  resume() {
    if (this.audio && !this.isPlaying) {
      this.audio.play().then(() => {
        this.isPlaying = true
        this.shouldAutoResume = false
      }).catch(error => {
        console.error('继续播放BGM失败:', error)
      })
    }
  }
  
  // 检查是否应该自动恢复播放
  shouldResume() {
    return this.shouldAutoResume && this.audio && !this.isPlaying
  }

  // 切换播放/暂停
  toggle() {
    if (this.isPlaying) {
      this.pause()
    } else {
      if (this.audio) {
        this.resume()
      } else {
        this.play()
      }
    }
  }

  // 设置音量
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume))
    if (this.audio) {
      this.audio.volume = this.volume
    }
    // 保存到localStorage
    localStorage.setItem('bgm_volume', this.volume.toString())
  }

  // 获取音量
  getVolume() {
    const saved = localStorage.getItem('bgm_volume')
    if (saved !== null) {
      this.volume = parseFloat(saved)
    }
    return this.volume
  }

  // 切换到下一首
  next() {
    if (this.bgmList.length === 0) return
    
    this.currentIndex = (this.currentIndex + 1) % this.bgmList.length
    this.play(this.bgmList[this.currentIndex])
  }

  // 切换到上一首
  prev() {
    if (this.bgmList.length === 0) return
    
    this.currentIndex = (this.currentIndex - 1 + this.bgmList.length) % this.bgmList.length
    this.play(this.bgmList[this.currentIndex])
  }

  // 选择指定BGM
  selectBGM(index) {
    if (index >= 0 && index < this.bgmList.length) {
      this.currentIndex = index
      this.play(this.bgmList[index])
    }
  }

  // 获取当前BGM信息
  getCurrentBGM() {
    return this.currentBGM
  }

  // 获取BGM列表
  getBGMList() {
    return this.bgmList
  }
}

// 创建单例
const bgmManager = new BGMManager()

export default bgmManager

